import { renderHomeView } from './views/HomeView.js';
import { renderDashboardView } from './views/DashboardView.js';
import { renderUploadView } from './views/UploadView.js';
import { renderJobsView } from './views/JobsView.js';
import { renderConversationView } from './views/ConversationView.js';
import { renderAuthView } from './views/AuthView.js';
import { renderForgotPasswordView } from './views/ForgotPasswordView.js';
import { renderResetPasswordView } from './views/ResetPasswordView.js';
import { renderProfileView } from './views/ProfileView.js';
import { renderBillingView } from './views/BillingView.js';
import { renderAdminView } from './views/AdminView.js';
import { renderLiveSessionView } from './views/LiveSessionView.js';
import { renderLiveShareView } from './views/LiveShareView.js';
import { renderImprintView } from './views/ImprintView.js';
import { renderExportTemplatesView } from './views/ExportTemplatesView.js';
import { renderAppFactoryDashboardView } from './views/AppFactoryDashboardView.js';
import { renderAppFactoryNewView } from './views/AppFactoryNewView.js';
import { renderAppFactorySessionView } from './views/AppFactorySessionView.js';
import { renderAppFactoryReviewView } from './views/AppFactoryReviewView.js';
import { renderNewRequirementsView } from './views/NewRequirementsView.js';
import { renderRequirementsListView } from './views/RequirementsListView.js';
import { renderCustomersProjectsView } from './views/CustomersProjectsView.js';
import { renderSalesView } from './views/SalesView.js';
import { checkReleaseNotesOverlay } from './views/ReleaseNotesOverlay.js';
import { getCurrentUser, logout, whenAuthReady } from './auth.js';
import { AVAILABLE_LANGUAGES, getLanguage, getStoredLanguage, setLanguage, translate } from './i18n.js';
import { isDemoMode } from './demo/runtime.js';
import { getGeoLanguage } from './api.js';
import { canAccessAppFactory, canAccessLive, isRouteAllowed } from './versionAccess.js';

const app = document.getElementById('app');
const homeLink = document.getElementById('home-link');
const dashboardLink = document.getElementById('dashboard-link');
const uploadLink = document.getElementById('upload-link');
const newRequirementsLink = document.getElementById('new-requirements-link');
const requirementsLink = document.getElementById('requirements-link');
const customersProjectsLink = document.getElementById('customers-projects-link');
const jobsLink = document.getElementById('jobs-link');
const liveLink = document.getElementById('live-link');
const appFactoryLink = document.getElementById('app-factory-link');
const loginLink = document.getElementById('login-link');
const registerLink = document.getElementById('register-link');
const userActions = document.getElementById('user-actions');
const adminLink = document.getElementById('admin-link');
const salesLink = document.getElementById('sales-link');
const profileLink = document.getElementById('profile-link');
const billingLink = document.getElementById('billing-link');
const imprintLink = document.getElementById('imprint-link');
const sidebarFooter = document.querySelector('.sidebar-footer');
const logoutSlot = document.getElementById('logout-slot');
const sidebar = document.getElementById('sidebar');
const mobileNavToggle = document.getElementById('mobile-nav-toggle');
const mobileNavBackdrop = document.getElementById('mobile-nav-backdrop');

const THEME_KEY = 'anforderungsai:theme';
const VIEW_CLEANUP_EVENT = 'anforderungsai:view-register-cleanup';
let currentViewCleanup = null;

const runCurrentViewCleanup = () => {
  if (typeof currentViewCleanup === 'function') {
    try {
      currentViewCleanup();
    } catch (error) {
      console.warn('View cleanup failed', error);
    }
  }
  currentViewCleanup = null;
};

window.addEventListener(VIEW_CLEANUP_EVENT, (event) => {
  runCurrentViewCleanup();
  if (typeof event.detail === 'function') {
    currentViewCleanup = event.detail;
  }
});

const loadPreference = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    return value ?? fallback;
  } catch {
    return fallback;
  }
};

const savePreference = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore storage failures
  }
};

const isMobileNavOpen = () => document.body.dataset.mobileNavOpen === 'true';

const updateMobileNavToggle = () => {
  if (!mobileNavToggle) return;
  const open = isMobileNavOpen();
  mobileNavToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  const labelKey = open ? 'sidebar.menuClose' : 'sidebar.menuOpen';
  mobileNavToggle.setAttribute('aria-label', translate(labelKey));
};

const setMobileNavOpen = (next) => {
  if (next) {
    document.body.dataset.mobileNavOpen = 'true';
  } else {
    delete document.body.dataset.mobileNavOpen;
  }
  updateMobileNavToggle();
};

const routes = [
  { pattern: /^#?\/?$/, requiresAuth: false, render: () => renderHomeView(app) },
  { pattern: /^#\/home$/, requiresAuth: false, render: () => renderHomeView(app) },
  { pattern: /^#\/dashboard$/, requiresAuth: true, render: () => renderDashboardView(app) },
  { pattern: /^#\/requirements$/, requiresAuth: true, render: () => renderRequirementsListView(app) },
  { pattern: /^#\/customers-projects$/, requiresAuth: true, render: () => renderCustomersProjectsView(app) },
  { pattern: /^#\/upload$/, requiresAuth: true, render: () => renderUploadView(app) },
  { pattern: /^#\/new-requirements$/, requiresAuth: true, render: () => renderNewRequirementsView(app) },
  { pattern: /^#\/jobs$/, requiresAuth: true, render: () => renderJobsView(app) },
  { pattern: /^#\/conversation\/([^/]+)$/, requiresAuth: true, render: (match) => renderConversationView(app, match[1]) },
  { pattern: /^#\/profile$/, requiresAuth: true, render: () => renderProfileView(app) },
  { pattern: /^#\/billing$/, requiresAuth: true, render: () => renderBillingView(app) },
  { pattern: /^#\/export-templates$/, requiresAuth: true, render: () => renderExportTemplatesView(app) },
  { pattern: /^#\/live$/, requiresAuth: true, render: () => renderLiveSessionView(app) },
  { pattern: /^#\/live\/share\/([^/]+)$/, requiresAuth: false, render: (match) => renderLiveShareView(app, match[1]) },
  { pattern: /^#\/app-factory$/, requiresAuth: true, requiresAppFactoryAccess: true, render: () => renderAppFactoryDashboardView(app) },
  { pattern: /^#\/app-factory\/new$/, requiresAuth: true, requiresAppFactoryAccess: true, render: () => renderAppFactoryNewView(app) },
  { pattern: /^#\/app-factory\/session\/([^/]+)$/, requiresAuth: true, requiresAppFactoryAccess: true, render: (match) => renderAppFactorySessionView(app, match[1]) },
  { pattern: /^#\/app-factory\/review\/([^/]+)$/, requiresAuth: true, requiresAppFactoryAccess: true, render: (match) => renderAppFactoryReviewView(app, match[1]) },
  { pattern: /^#\/admin(?:\?.*)?$/, requiresAuth: true, requiresRole: 'admin', render: () => renderAdminView(app) },
  { pattern: /^#\/sales$/, requiresAuth: true, requiresRole: 'admin', render: () => renderSalesView(app) },
  { pattern: /^#\/login$/, requiresAuth: false, render: () => renderAuthView(app) },
  { pattern: /^#\/register$/, requiresAuth: false, render: () => renderAuthView(app, { initialMode: 'register' }) },
  { pattern: /^#\/forgot-password(?:\?.*)?$/, requiresAuth: false, render: () => renderForgotPasswordView(app) },
  { pattern: /^#\/reset-password(?:\?.*)?$/, requiresAuth: false, render: () => renderResetPasswordView(app) },
  { pattern: /^#\/impressum$/, requiresAuth: false, render: () => renderImprintView(app) },
];

const LOGIN_HASH = '#/login';
const REGISTER_HASH = '#/register';

const applyThemePreference = (theme) => {
  if (theme === 'light') {
    document.documentElement.dataset.theme = 'light';
  } else if (theme === 'dark') {
    document.documentElement.dataset.theme = 'dark';
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
};

const setThemePreference = (theme) => {
  const next = ['light', 'dark', 'system'].includes(theme) ? theme : 'system';
  savePreference(THEME_KEY, next);
  applyThemePreference(next);
  document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
    button.classList.toggle('active', button.getAttribute('data-theme-toggle') === next);
  });
};

const initThemeControls = () => {
  const current = loadPreference(THEME_KEY, 'system');
  applyThemePreference(current);
  document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
    button.classList.toggle('active', button.getAttribute('data-theme-toggle') === current);
    button.addEventListener('click', () => {
      const next = button.getAttribute('data-theme-toggle');
      setThemePreference(next);
    });
  });
};

const updateLanguageButtons = () => {
  const current = getLanguage();
  document.querySelectorAll('[data-language-toggle]').forEach((button) => {
    button.classList.toggle('active', button.getAttribute('data-language-toggle') === current);
  });
};

const initLanguageControls = () => {
  updateLanguageButtons();
  document.querySelectorAll('[data-language-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const next = button.getAttribute('data-language-toggle');
      setLanguage(next);
      updateLanguageButtons();
    });
  });
};

const initPreferenceControls = () => {
  initThemeControls();
  initLanguageControls();
};

const initMobileNavControls = () => {
  updateMobileNavToggle();
  if (mobileNavToggle) {
    mobileNavToggle.addEventListener('click', () => {
      setMobileNavOpen(!isMobileNavOpen());
    });
  }
  if (mobileNavBackdrop) {
    mobileNavBackdrop.addEventListener('click', () => {
      setMobileNavOpen(false);
    });
  }
  if (sidebar) {
    sidebar.addEventListener('click', (event) => {
      const target = event.target;
      if (!target) return;
      const closeTrigger = target.closest('.sidebar-nav a, .sidebar-footer-actions a, #logout-button, #version-switch-button');
      if (closeTrigger) {
        setMobileNavOpen(false);
      }
    });
  }
};

const initAutoLanguage = async () => {
  if (getStoredLanguage()) return;
  try {
    const result = await getGeoLanguage();
    const candidate = result?.language;
    if (candidate) {
      setLanguage(candidate);
    }
  } catch (error) {
    console.warn('Geo language detection failed', error);
  }
};

const updateSidebarCopy = () => {
  const sidebarTagline = document.querySelector('[data-sidebar-tagline]');
  if (sidebarTagline) {
    sidebarTagline.textContent = translate('sidebar.tagline');
  }
  const navLabel = document.querySelector('[data-nav-label="navigation"]');
  if (navLabel) {
    navLabel.textContent = translate('sidebar.navigationLabel');
  }
  const accessLabel = document.querySelector('[data-nav-label="access"]');
  if (accessLabel) {
    accessLabel.textContent = translate('sidebar.accessLabel');
  }
  const themeLabel = document.querySelector('[data-theme-label]');
  if (themeLabel) {
    themeLabel.textContent = translate('sidebar.themeLabel');
  }
  const languageLabel = document.querySelector('[data-language-label]');
  if (languageLabel) {
    languageLabel.textContent = translate('sidebar.languageLabel');
  }
  if (homeLink) homeLink.textContent = translate('nav.home');
  if (dashboardLink) dashboardLink.textContent = translate('nav.dashboard');
  if (requirementsLink) requirementsLink.textContent = translate('nav.requirements');
  if (customersProjectsLink) customersProjectsLink.textContent = translate('nav.customersProjects');
  if (uploadLink) uploadLink.textContent = translate('nav.upload');
  if (newRequirementsLink) newRequirementsLink.textContent = translate('nav.newRequirements');
  if (jobsLink) jobsLink.textContent = translate('nav.jobs');
  if (liveLink) liveLink.textContent = translate('nav.live');
  if (appFactoryLink) appFactoryLink.textContent = translate('nav.appFactory') ?? 'App Factory';
  if (profileLink) profileLink.textContent = translate('nav.profile');
  if (adminLink) adminLink.textContent = translate('nav.admin');
  if (salesLink) salesLink.textContent = 'Sales';
  if (imprintLink) imprintLink.textContent = translate('nav.imprint');
  if (loginLink) loginLink.textContent = translate('nav.login');
  if (registerLink) registerLink.textContent = translate('nav.register');
  document.querySelectorAll('[data-admin-only]').forEach((el) => {
    el.style.display = 'none';
  });
  if (getCurrentUser()?.role === 'admin') {
    document.querySelectorAll('[data-admin-only]').forEach((el) => {
      el.style.display = '';
    });
  }
  updateMobileNavToggle();
};

const updateHeader = () => {
  updateSidebarCopy();
  const user = getCurrentUser();
  if (user) {
    if (homeLink) {
      homeLink.style.display = 'none';
    }
    if (loginLink) {
      loginLink.style.display = 'none';
    }
    if (registerLink) {
      registerLink.style.display = 'none';
    }
    if (dashboardLink) {
      dashboardLink.style.display = '';
    }
    if (requirementsLink) {
      requirementsLink.style.display = '';
    }
    if (customersProjectsLink) {
      customersProjectsLink.style.display = '';
    }
    if (profileLink) {
      profileLink.style.display = '';
    }
    if (billingLink) {
      billingLink.style.display = '';
    }
    if (uploadLink) {
      uploadLink.style.display = 'none';
    }
    if (newRequirementsLink) {
      newRequirementsLink.style.display = '';
    }
    if (jobsLink) {
      jobsLink.style.display = '';
    }
    if (liveLink) {
      liveLink.style.display = canAccessLive(user) ? '' : 'none';
    }
    if (appFactoryLink) {
      appFactoryLink.style.display = canAccessAppFactory(user) ? '' : 'none';
    }
    if (adminLink) {
      adminLink.style.display = user.role === 'admin' ? '' : 'none';
    }
    if (salesLink) {
      salesLink.style.display = user.role === 'admin' ? '' : 'none';
    }
    if (userActions) {
      const nameParts = [
        user.profile?.firstName?.trim(),
        user.profile?.lastName?.trim(),
      ].filter(Boolean);
      const displayName = nameParts.length ? nameParts.join(' ') : user.email;
      const isCollapsed = sidebarFooter?.dataset.collapsed === 'true';
      userActions.innerHTML = `
        <div class="user-meta">
          <div class="user-meta-header">
            <span class="muted small">${translate('sidebar.loggedInLabel')}</span>
            <button
              type="button"
              class="sidebar-collapse-toggle"
              aria-label="Bereich ein-/ausklappen"
              aria-expanded="${isCollapsed ? 'false' : 'true'}"
              aria-controls="sidebar-collapsible"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" role="img" aria-hidden="true">
                <line x1="4" y1="6" x2="20" y2="6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></line>
                <circle cx="9" cy="6" r="2" stroke="currentColor" stroke-width="1.6" fill="none"></circle>
                <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></line>
                <circle cx="15" cy="12" r="2" stroke="currentColor" stroke-width="1.6" fill="none"></circle>
                <line x1="4" y1="18" x2="20" y2="18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></line>
                <circle cx="11" cy="18" r="2" stroke="currentColor" stroke-width="1.6" fill="none"></circle>
              </svg>
            </button>
          </div>
          <strong>${displayName}</strong>
        </div>
        <div class="user-extra">
          ${user.plan?.name ? `<span class="pill" style="background-color: rgba(56,189,248,0.2); color: #38bdf8;">${user.plan.name}</span>` : ''}
        </div>
      `;
      const collapseToggle = userActions.querySelector('.sidebar-collapse-toggle');
      if (collapseToggle && sidebarFooter) {
        collapseToggle.addEventListener('click', () => {
          const collapsed = sidebarFooter.dataset.collapsed === 'true';
          if (collapsed) {
            sidebarFooter.removeAttribute('data-collapsed');
            collapseToggle.setAttribute('aria-expanded', 'true');
          } else {
            sidebarFooter.dataset.collapsed = 'true';
            collapseToggle.setAttribute('aria-expanded', 'false');
          }
        });
      }
    }
    if (logoutSlot) {
      const logoutLabel = isDemoMode() ? translate('sidebar.logoutDemo') : translate('sidebar.logout');
      logoutSlot.innerHTML = `<button type="button" id="logout-button" class="sidebar-logout-button">${logoutLabel}</button>`;
      const logoutButton = logoutSlot.querySelector('#logout-button');
      if (logoutButton) {
        logoutButton.addEventListener('click', async () => {
          const wasDemo = isDemoMode();
          await logout();
          if (!wasDemo) {
            window.location.hash = LOGIN_HASH;
          }
        });
      }
    }
  } else {
    if (homeLink) {
      homeLink.style.display = '';
    }
    if (loginLink) {
      loginLink.style.display = '';
    }
    if (registerLink) {
      registerLink.style.display = '';
    }
    if (dashboardLink) {
      dashboardLink.style.display = 'none';
    }
    if (requirementsLink) {
      requirementsLink.style.display = 'none';
    }
    if (customersProjectsLink) {
      customersProjectsLink.style.display = 'none';
    }
    if (profileLink) {
      profileLink.style.display = 'none';
    }
    if (billingLink) {
      billingLink.style.display = 'none';
    }
    if (adminLink) {
      adminLink.style.display = 'none';
    }
    if (salesLink) {
      salesLink.style.display = 'none';
    }
    if (uploadLink) {
      uploadLink.style.display = 'none';
    }
    if (newRequirementsLink) {
      newRequirementsLink.style.display = 'none';
    }
    if (jobsLink) {
      jobsLink.style.display = 'none';
    }
    if (liveLink) {
      liveLink.style.display = 'none';
    }
    if (appFactoryLink) {
      appFactoryLink.style.display = 'none';
    }
    if (userActions) {
      userActions.innerHTML = '';
    }
    if (logoutSlot) {
      logoutSlot.innerHTML = '';
    }
    if (sidebarFooter) {
      sidebarFooter.dataset.collapsed = 'true';
    }
  }
};

const dispatch = () => {
  runCurrentViewCleanup();
  let hash = window.location.hash || '#/';
  const cleanHash = hash.split('?')[0];
  const user = getCurrentUser();
  const route = routes.find((candidate) => cleanHash.match(candidate.pattern));

  if (!user && (!route || route.requiresAuth !== false)) {
    if (hash !== LOGIN_HASH) {
      window.location.hash = LOGIN_HASH;
    } else {
      renderAuthView(app);
    }
    return;
  }

  if (user && (cleanHash === LOGIN_HASH || cleanHash === REGISTER_HASH)) {
    window.location.hash = '#/dashboard';
    return;
  }

  if (route?.requiresRole && user?.role !== route.requiresRole) {
    window.location.hash = '#/dashboard';
    return;
  }

  if (user && !isRouteAllowed(cleanHash, user)) {
    window.location.hash = '#/dashboard';
    return;
  }

  if (route?.requiresAppFactoryAccess && !canAccessAppFactory(user)) {
    window.location.hash = '#/dashboard';
    return;
  }

  if (!route) {
    window.location.hash = '#/dashboard';
    return;
  }

  const match = cleanHash.match(route.pattern);
  try {
    const result = route.render(match);
    if (result instanceof Promise) {
      result.catch((error) => {
        console.error('View rendering failed', error);
        app.innerHTML = `<div class="card"><p class="muted">Fehler beim Rendern: ${error.message}</p></div>`;
      });
    }
  } catch (error) {
    console.error('View rendering failed', error);
    app.innerHTML = `<div class="card"><p class="muted">Fehler beim Rendern: ${error.message}</p></div>`;
  }
};

initPreferenceControls();
initAutoLanguage();
initMobileNavControls();

// Initialize version switch button
const versionSwitchButton = document.getElementById('version-switch-button');
if (versionSwitchButton) {
  versionSwitchButton.addEventListener('click', () => {
    try {
      localStorage.setItem('anforderungsai_version', 'new');
      window.location.href = '/new/#/';
    } catch (error) {
      console.error('Failed to switch version:', error);
    }
  });
}

window.addEventListener('hashchange', () => {
  if (isMobileNavOpen()) setMobileNavOpen(false);
  dispatch();
});
window.addEventListener('anforderungsai:auth-changed', () => {
  updateHeader();
  dispatch();
  checkReleaseNotesOverlay();
});
window.addEventListener('anforderungsai:language-changed', () => {
  updateLanguageButtons();
  updateHeader();
  dispatch();
});

whenAuthReady()
  .catch((error) => {
    console.warn('Auth initialization failed', error);
  })
  .finally(() => {
    updateHeader();
    dispatch();
    checkReleaseNotesOverlay();
  });
